import { useQuery } from "@tanstack/react-query";
import { api, getApiErrorMessage } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from "../../lib/load-failure";

/**
 * The driver picker for a trucking load (fe#815 driver assignment).
 *
 * Lists the tenant's ACTIVE drivers from GET /trucking/drivers/availability
 * (the route leaves out retired and inactive ones). Each row carries the API's
 * `licence_expired`, decided on the same rule as the 409 DRIVER_LICENCE_EXPIRED
 * refusal (valid through the expiry day, in the tenant's zone, api#1130). The
 * picker FLAGS an expired licence and still lets it be chosen: the API decides,
 * and the screen shows its refusal as sent. Nothing here compares dates.
 *
 * `licence_expired: null` means no expiry is on file. That is not a valid
 * licence and not an expired one, so it is named as the gap it is.
 *
 * The driver is optional: a load can be booked with no driver and given one
 * later. A list that failed to load says so and keeps "No driver" and the
 * load's current driver as the choices, rather than reading as no drivers.
 */
export interface DriverOption {
  id: string;
  name: string | null;
  licence_expired: boolean | null;
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);

const toOption = (r: Record<string, unknown>): DriverOption => ({
  id: String(r.id ?? ""),
  name: str(r.name),
  licence_expired: typeof r.licence_expired === "boolean" ? r.licence_expired : null,
});

export function useDriverOptions() {
  return useQuery({
    queryKey: ["trucking-driver-options"],
    queryFn: () =>
      api.trucking.driverAvailability().then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toOption)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** "Mike Kowalski — licence expired", "Pat Keller — no licence date", "Cody Rempel". */
export function driverOptionLabel(d: DriverOption): string {
  const name = d.name ?? "—";
  if (d.licence_expired === true) return `${name} — licence expired`;
  if (d.licence_expired === null) return `${name} — no licence date`;
  return name;
}

interface Props {
  id: string;
  value: string;
  onChange: (driverId: string) => void;
  /** The load's current driver, kept as a choice when the list cannot name them. */
  currentId?: string;
  currentName?: string | null;
  className?: string;
}

export default function DriverPicker({ id, value, onChange, currentId, currentName, className }: Props) {
  const query = useDriverOptions();
  const failed = queryLoadFailure(query) != null;
  const drivers = failed ? undefined : query.data;
  const currentMissing = !!currentId && !(drivers ?? []).some((d) => d.id === currentId);

  return (
    <>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={query.isLoading && !failed}
        className={className}
      >
        <option value="">No driver</option>
        {currentMissing && <option value={currentId}>{currentName ?? "Current driver"}</option>}
        {(drivers ?? []).map((d) => (
          <option key={d.id} value={d.id}>
            {driverOptionLabel(d)}
          </option>
        ))}
      </select>
      {failed ? (
        <p role="status" className="mt-1 text-[11px] text-fieldiq-muted">
          The driver list could not be loaded. The load can be saved without a driver.
        </p>
      ) : drivers && drivers.length === 0 ? (
        <p className="mt-1 text-[11px] text-fieldiq-muted">No active drivers. The load can be saved without a driver.</p>
      ) : null}
    </>
  );
}

/** Look a chosen driver up in the shared query, for a confirmation summary. */
export function useDriverOption(driverId: string): DriverOption | null {
  const query = useDriverOptions();
  if (!driverId || queryLoadFailure(query) != null) return null;
  return query.data?.find((d) => d.id === driverId) ?? null;
}

/**
 * The words to show for a refused load write. Every refusal the API sends
 * puts its sentence in `error` (DRIVER_LICENCE_EXPIRED, LOAD_DRIVER_LOCKED, a
 * fleet refusal) except the HOS check on POST, which sends the CODE in `error`
 * (`'HOS_VIOLATION'`) and the sentence in `message`. Shown as sent, that would
 * read "HOS_VIOLATION", so for that one shape the sentence is taken from where
 * the API put it.
 */
export function loadWriteRefusal(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
  if (data?.error === "HOS_VIOLATION" && typeof data.message === "string" && data.message) return data.message;
  return getApiErrorMessage(err, fallback);
}
